import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import { TemplateEditorDrawerProps } from './types';
import FieldManager from './FieldManager';

const TemplateEditorDrawer: React.FC<TemplateEditorDrawerProps> = ({
  isOpen,
  onClose,
  template,
  formData,
  onFormChange,
  onSave,
  onPreview,
  fieldDefinitions,
  saving,
  hasUnsavedChanges,
  autoSaving = false,
  lastAutoSave = null
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(1);

  // Reset step when closing
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      
      if (firstElement) {
        setTimeout(() => firstElement.focus(), 100);
      }

      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, currentStep]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, hasUnsavedChanges]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onFormChange({ [name]: value });
  };

  const handleFieldConfigChange = (fieldKey: string, property: 'visible' | 'required', value: boolean) => {
    const currentField = formData.fieldsConfig?.fields?.[fieldKey] || {
      visible: false,
      required: false,
      order: Object.keys(formData.fieldsConfig?.fields || {}).length + 1
    };

    const updatedFields = { ...formData.fieldsConfig.fields };
    
    if (property === 'visible' && !value && currentField.required) {
      updatedFields[fieldKey] = { ...currentField, visible: false, required: false };
    } else {
      updatedFields[fieldKey] = { ...currentField, [property]: value };
    }

    onFormChange({ fieldsConfig: { fields: updatedFields } });
  };

  const handleReorderFields = (sourceIndex: number, targetIndex: number) => {
    const fields = formData.fieldsConfig?.fields || {};
    const visibleFields = Object.entries(fields)
      .filter(([_, config]) => config.visible)
      .sort(([, a], [, b]) => a.order - b.order);

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex >= visibleFields.length || targetIndex >= visibleFields.length) {
      return;
    }

    const newFields = { ...fields };
    const sourceField = visibleFields[sourceIndex];
    const targetField = visibleFields[targetIndex];

    if (sourceIndex < targetIndex) {
      for (let i = sourceIndex + 1; i <= targetIndex; i++) {
        const field = visibleFields[i];
        newFields[field[0]] = { ...newFields[field[0]], order: newFields[field[0]].order - 1 };
      }
    } else {
      for (let i = targetIndex; i < sourceIndex; i++) {
        const field = visibleFields[i];
        newFields[field[0]] = { ...newFields[field[0]], order: newFields[field[0]].order + 1 };
      }
    }

    newFields[sourceField[0]] = { ...newFields[sourceField[0]], order: targetField[1].order };
    onFormChange({ fieldsConfig: { fields: newFields } });
  };

  const handleQuickAction = (action: 'essential' | 'showAll' | 'hideAll') => {
    const newFields = { ...formData.fieldsConfig.fields };
    
    if (action === 'essential') {
      const essentialFields = ['first_name', 'last_name', 'email', 'phone', 'company'];
      Object.keys(fieldDefinitions).forEach(fieldKey => {
        if (newFields[fieldKey]) {
          newFields[fieldKey] = {
            ...newFields[fieldKey],
            visible: essentialFields.includes(fieldKey),
            required: ['first_name', 'last_name', 'email'].includes(fieldKey)
          };
        }
      });
    } else if (action === 'showAll') {
      Object.keys(newFields).forEach(fieldKey => {
        newFields[fieldKey] = { ...newFields[fieldKey], visible: true };
      });
    } else if (action === 'hideAll') {
      Object.keys(newFields).forEach(fieldKey => {
        newFields[fieldKey] = { ...newFields[fieldKey], visible: false, required: false };
      });
    }

    onFormChange({ fieldsConfig: { fields: newFields } });
  };

  const visibleFieldsCount = Object.values(formData.fieldsConfig?.fields || {}).filter(f => f.visible).length;
  const canProceed = currentStep === 1 ? formData.name.trim().length > 0 : visibleFieldsCount > 0;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden bg-white"
      aria-labelledby="editor-title"
      role="dialog"
      aria-modal="true"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div className="h-full w-full flex flex-col" ref={drawerRef}>
        {/* Clean White Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-5">
            <div className="flex items-center justify-between">
              {/* Left: Back & Title */}
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  aria-label="Back to templates"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Templates
                </button>
                
                <div className="border-l border-gray-300 pl-4">
                  <h1 id="editor-title" className="text-xl font-bold text-gray-900">
                    {template ? 'Edit Template' : 'Create New Template'}
                  </h1>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {currentStep === 1 ? 'Step 1 of 2' : 'Step 2 of 2'} • {currentStep === 1 ? 'Basic Details' : 'Field Configuration'}
                  </p>
                </div>
              </div>

              {/* Right: Actions & Status */}
              <div className="flex items-center space-x-3">
                {autoSaving && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-600 border-t-transparent mr-1.5" />
                    Auto-saving...
                  </span>
                )}

                {currentStep === 2 && (
                  <button
                    type="button"
                    onClick={onPreview}
                    className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <EyeIcon className="h-5 w-5 mr-2" />
                    Preview Form
                  </button>
                )}
                
                {currentStep === 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    disabled={!canProceed}
                    className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    Continue
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onSave}
                    disabled={saving || !canProceed}
                    className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {saving && (
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {saving ? 'Saving...' : (template ? 'Save Changes' : 'Create Template')}
                  </button>
                )}
              </div>
            </div>

            {/* Clean Progress Bar */}
            <div className="mt-5 flex items-center">
              <div className="flex items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all border-2 ${currentStep >= 1 ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-400'}`}>
                  {currentStep > 1 ? '✓' : '1'}
                </div>
                <span className={`ml-3 text-sm font-medium ${currentStep >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>
                  Basic Details
                </span>
              </div>
              
              <div className="flex-1 mx-6">
                <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full bg-blue-600 transition-all duration-500 ${currentStep === 2 ? 'w-full' : 'w-0'}`} />
                </div>
              </div>
              
              <div className="flex items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all border-2 ${currentStep === 2 ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-400'}`}>
                  2
                </div>
                <span className={`ml-3 text-sm font-medium ${currentStep === 2 ? 'text-gray-900' : 'text-gray-400'}`}>
                  Field Configuration
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area - Clean & Spacious */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="max-w-5xl mx-auto px-6 py-8">
            {currentStep === 1 ? (
              /* Step 1: Basic Info - Simple & Clean */
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="template-name" className="block text-base font-semibold text-gray-900 mb-2">
                        Template Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="template-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="e.g., Lead Capture Form, Quick Registration, Event Sign-up"
                        maxLength={100}
                        required
                        autoFocus
                      />
                      <p className="mt-2 text-sm text-gray-500">Choose a clear, descriptive name</p>
                    </div>

                    <div>
                      <label htmlFor="template-description" className="block text-base font-semibold text-gray-900 mb-2">
                        Description <span className="text-gray-400 text-sm font-normal">(Optional)</span>
                      </label>
                      <textarea
                        id="template-description"
                        name="description"
                        value={formData.description}
                        onChange={handleFormChange}
                        rows={4}
                        className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Describe when to use this template and what makes it unique..."
                      />
                      <p className="mt-2 text-sm text-gray-500">Help your team understand when to use this template</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-300 rounded-xl p-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Quick Tips</h4>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li>• Use specific names like "Event Registration" instead of "Form 1"</li>
                        <li>• The description helps team members choose the right template</li>
                        <li>• You'll configure which fields to show in the next step</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Step 2: Field Configuration - Clean & Organized */
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">Configure Form Fields</h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Select which fields to display and mark required fields
                        </p>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg">
                          <span className="font-semibold text-gray-900">{visibleFieldsCount}</span>
                          <span className="text-gray-600 ml-1">of {Object.keys(fieldDefinitions).length} fields selected</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <FieldManager
                      fieldDefinitions={fieldDefinitions}
                      fieldsConfig={formData.fieldsConfig?.fields || {}}
                      onFieldConfigChange={handleFieldConfigChange}
                      onReorderFields={handleReorderFields}
                      onQuickAction={handleQuickAction}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-start">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to Step 1
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default TemplateEditorDrawer;
